import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { groupTasksByStatus } from '../utils/groupTasksByStatus'
import { useStatuses } from '../hooks/useWorkspaceStatuses'
import { ListDetailsView } from './ListDetailsView'
import type { ListDetailsViewSection } from './ListDetailsView'
import type { TaskViewContext } from './TaskPageLayout'
import { StatusIcon } from '@/components/app/indicators/StatusIcon'

/**
 * A task list grouped by status (design 1c / 6a): one collapsible section per
 * status, active work first, closed work collapsed at the bottom.
 */
export function StatusGroupedTaskView(context: TaskViewContext) {
  const { t } = useTranslation('tasks')
  const { all } = useStatuses()

  const sections = useMemo<Array<ListDetailsViewSection>>(() => {
    const groups = groupTasksByStatus(context.items, all.statuses)
    const grouped = new Set(groups.flatMap((g) => g.tasks.map((task) => task.id)))
    const leftovers = context.items.filter((task) => !grouped.has(task.id))

    const result: Array<ListDetailsViewSection> = groups.map((group) => ({
      id: `status-${group.status.id}`,
      title: group.status.name,
      icon: <StatusIcon progressType={group.status.progressType} statusId={group.status.id} size="sm" />,
      items: group.tasks,
      collapsible: true,
      defaultOpen: !group.isClosed,
      onAdd: context.openAddTask ? () => context.openAddTask?.({ statusId: group.status.id }) : undefined,
    }))
    // Tasks whose status hasn't synced yet shouldn't vanish from the list.
    if (leftovers.length > 0) {
      result.push({
        id: 'status-other',
        title: t('grouping.otherStatus'),
        items: leftovers,
        collapsible: true,
        defaultOpen: true,
      })
    }
    return result
  }, [context.items, context.openAddTask, all.statuses, t])

  return <ListDetailsView {...context} sections={sections} />
}
